import { PropsWithChildren, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";

import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import Container from "@mui/material/Container";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";

import { useTheme } from "@mui/material/styles";

import { GoBackButton, IconButton } from "@covertree/ui";

interface Props extends PropsWithChildren {
  canGoBack: boolean;
  drawerNavigationItems: { name: string; path: string }[];
  isBackButtonDisabled: boolean;
  onClose(): void;
  title: string;
}

export function DrawerFormLayout({
  canGoBack,
  children,
  drawerNavigationItems,
  isBackButtonDisabled,
  onClose,
  title,
}: Props) {
  const { pathname } = useLocation();

  const theme = useTheme();

  const currentStepIndex = useMemo(
    () =>
      drawerNavigationItems.findIndex(({ path }) => pathname.includes(path)),
    [pathname, drawerNavigationItems]
  );

  const stepColor = useCallback(
    (stepIndex: number): "primary" | "textSecondary" | "inherit" => {
      if (currentStepIndex === stepIndex) {
        return "primary";
      }
      if (currentStepIndex < stepIndex) {
        return "textSecondary";
      }
      return "inherit";
    },
    [currentStepIndex]
  );

  return (
    <Drawer
      anchor="bottom"
      open
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: theme.spacing(3, 3, 0, 0) },
      }}>
      <Container sx={{ minWidth: "100%", height: "90vh" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(15, 1fr)",
          }}>
          <Box
            justifyContent="space-between"
            display="flex"
            alignItems="center"
            position="sticky"
            top={0}
            gap={theme.spacing(2)}
            sx={{
              gridColumnStart: 2,
              gridColumnEnd: "span 12",
              backgroundColor: "background.default",
              zIndex: 100,
            }}>
            <Typography variant="h3">{title}</Typography>
          </Box>

          <Box
            justifyContent="flex-end"
            display="flex"
            alignItems="center"
            p={theme.spacing(4)}
            position="sticky"
            top={0}
            gap={theme.spacing(2)}
            sx={{ gridColumnEnd: "15" }}>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              gridColumnStart: 2,
              gridColumnEnd: "span 3",
              zIndex: 100,
            }}
            display="flex"
            flexDirection="column">
            <Box position="fixed">
              <Box display="flex" flexDirection="column">
                {drawerNavigationItems.map((item, index) => (
                  <Typography
                    key={item.path}
                    p={theme.spacing(0.5, 0)}
                    sx={{
                      "&.active, &:hover": {
                        textDecoration: "none",
                      },
                    }}
                    color={stepColor(index)}>
                    {item.name}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              gridColumnStart: 5,
              gridColumnEnd: "span 8",
              marginBottom: theme.spacing(3),
            }}>
            {canGoBack && (
              <GoBackButton
                disabled={isBackButtonDisabled}
                buttonStyles={{
                  justifyContent: "flex-start",
                  position: "sticky",
                  top: 100,
                  width: "100%",
                  backgroundColor: "background.default",
                  borderRadius: 0,
                  "&:hover": {
                    backgroundColor: "background.default",
                  },
                  zIndex: 100,
                }}
              />
            )}
            {children}
          </Box>
        </Box>
      </Container>
    </Drawer>
  );
}
